/**
 * Created by jv on 2019-08-20 (13:34).
 */
import utils from "Sources/utils/utils";
import AppError from "Sources/models/app-error";
import defaultLayout from "Sources/components/business-model/layouts/default";
import InvestmentsView from "../game/components/investments-view";

import Shared from "Sources/shared";
import InfoWasteDepotView from "Sources/components/popup/info-waste-depot";
import InfoMarketView from "Sources/components/popup/info-market";
import InfoMiningView from "Sources/components/popup/info-mining";

const dashboardIcons = require.context("Images/business-model/dashboard", false, /\.svg$/);
const resourceIcons = require.context("Images/business-model/resource-icons", false, /\.svg$/);

const log = require("Sources/utils/logger")();

const fabric = require("fabric").fabric;

const INACTIVE_OPACITY = 0.25;
const UNDER_CONSTRUCTION_OPACITY = 0.5;

class BusinessModel
{
    constructor(options)
    {
        if (utils.isNull(options)) throw new AppError({ exception: new Error("Options for BusinessModel cannot be null") });

        this.id = options.id || utils.generateElementId("business-model-canvas-");

        this.containerElement = $(options.containerElement);
        this.availableCanvasSize = options.availableCanvasSize;
        this.onCanvasSizeChanged = options.onCanvasSizeChanged;
        this.roundOverview = options.roundOverview;
        this.team = options.team;
        this.factories = options.factories;
        this.view = options.view;

        this.layoutData = utils.isNotNull(options.layoutData) ? options.layoutData : defaultLayout;

        this.sectorsGroup = null;
        this.sectors = [];

        this.infoIcons = [];
        this.infoIconsVisible = utils.isNotNull(options.infoIconsVisible) ? options.infoIconsVisible : false;

        this.employeeSpotGroup = null;
        this.employeeSpots = [];

        this.canvasRendered = false;
        this.onRenderComplete = options.onRenderComplete;
        this.onInfoScreenRequested = options.onInfoScreenRequested;
        this.onAssignEmployee = options.onAssignEmployee;

        this.debugGroup = null;

        this.shouldAbortAnimations = false;

        this.onInactiveSpotClick = options.onInactiveSpotClick;
        this.onAssignMineEmployee = options.onAssignMineEmployee;
        this.onAssignMicrochipEmployee = options.onAssignMicrochipEmployee;
        this.onAssignSecondaryEmployee = options.onAssignSecondaryEmployee;
        this.onAssignWasteRecycleEmployee = options.onAssignWasteRecycleEmployee;
        this.onAssignReuseEmployee = options.onAssignReuseEmployee;
        this.onAssignRentalEmployee = options.onAssignRentalEmployee;

    }

    render()
    {
        log.info("Rendering business model for layout %o", this.layoutData);

        this.createCanvas();

        this.debugGroup = new fabric.Group([], {
            hasBorders: false,
            hasControls: false,
            selectable: false,
            evented: false,
            reference: false,
            movable: false
        });

        this.canvas.add(this.debugGroup);

        this.sectorsGroup = new fabric.Group([], {
            hasBorders: false,
            hasControls: false,
            selectable: false,
            evented: false,
            reference: false,
            movable: false,

            objectCaching: false
        });

        this.employeeSpotGroup = new fabric.Group([], {
            hasBorders: false,
            hasControls: false,
            selectable: false,
            reference: false,
            movable: false,

            objectCaching: false,
            hoverCursor: "pointer"
        });

        this.canvas.add(this.sectorsGroup);
        this.canvas.add(this.employeeSpotGroup);

        this.renderAll();
    }

    async renderAll()
    {
        this.setUpAutoResize();

        await Promise.all([
            this.setUpMine(),
            this.setUpMicrochipFactory(),
            this.setUpSecondaryFactory(),
            this.setUpMarket(),
            this.setUpWasteDepot(),
            this.setUpWasteRecycling(),
            this.setUpReuseFacility(),
            this.setUpRental()
        ]);
    }

    _checkRenderComplete()
    {
        if (this.hasRendered())
        {
            // this.sectorsGroup.addWithUpdate();
            // this.sectorsGroup.setCoords();
            // this.sectorsGroup.bringToFront();

            this.debugGroup.bringToFront();

            this.canvas.renderAll();

            if (utils.isFunction(this.onRenderComplete))
            {
                this.onRenderComplete.call(this);
            }
        }
    }

    hasRendered()
    {
        let renderCompleted = this.canvasRendered;

        let sectorsRendered = true;

        for (let i = 0; i < this.sectors.length; i++)
        {
            sectorsRendered = sectorsRendered && this.sectors[i].hasRendered;
            if (!sectorsRendered) break;
        }

        renderCompleted = renderCompleted && sectorsRendered;

        return renderCompleted;
    }

    createCanvas()
    {
        log.info("Creating canvas for business model [%s]", this.id);

        if (utils.isNotNull(this.canvasEl))
        {
            this.containerElement[0].removeChild(this.canvasEl.parentNode);
            this.canvas = null;
            this.canvasEl = null;
        }

        this.canvasEl = document.createElement("canvas");
        this.canvasEl.id = this.id;

        this.containerElement.append(this.canvasEl);

        this.canvas = new fabric.Canvas(this.id, {
            backgroundColor: this.layoutData.backgroundColor || "white",

            width: this.layoutData.size.width,
            height: this.layoutData.size.height,

            imageSmoothingEnabled: false
        });

        this.canvas.selection = false;

        if (!utils.isNullOrEmpty(this.layoutData.background))
        {
            this.setUpBackground()
                .then(() =>
                {
                    this.canvasRendered = true;
                    this._checkRenderComplete();
                });
        }
        else
        {
            this.canvasRendered = true;
        }

        if (__DEVELOPMENT__)
        {
            // this.addGridToCanvas(50);
        }
    }

    setUpBackground()
    {
        if (!utils.isNullOrEmpty(this.layoutData.background))
        {
            let options = {
                top: -491,
                left: -341,

                evented: false,
                selectable: false,
                hasBorders: false,
                hasControls: false,
                reference: false
            };

            if (this.layoutData.background.endsWith("svg"))
            {
                return this.loadSvg(this.layoutData.background, options)
                    .then((fabricObj) =>
                    {
                        this.canvas.add(fabricObj);
                        this.canvas.sendToBack(fabricObj);

                        // fabricObj.scaleToWidth(this.canvas.width);
                        fabricObj.setCoords();
                        this.canvas.renderAll();

                        return fabricObj;
                    })
                    .catch((err) =>
                    {
                        log.error("Failed to load background image - %o", err);
                    });
            }
            else
            {
                return this.loadImage(this.layoutData.background, options)
                    .then((fabricObj) =>
                    {
                        this.canvas.add(fabricObj);
                        this.canvas.sendToBack(fabricObj);

                        // fabricObj.scaleToWidth(this.canvas.width);
                        fabricObj.setCoords();
                        this.canvas.renderAll();

                        return fabricObj;
                    })
                    .catch((err) =>
                    {
                        log.error("Failed to load background image - %o", err);
                    });
            }
        }

        return null;
    }

    async setUpMine()
    {
        const factoryName = "mine";
        const mine = await this.loadSvg(dashboardIcons("./mine.svg"), {
            left: 24,
            top: 528
        });

        await this.createEmployeeSpot({
            left: 274,
            top: 683,
            taken: this.getEmployeeCountForFactory(factoryName) >= 1,
            enabled: true,
            onAssignEmployee: () =>
            {
                this.onAssignMineEmployee.call(this);
            },
            namedGroup: "mine-1"
        });

        await this.createEmployeeSpot({
            left: 311,
            top: 698,
            taken: this.getEmployeeCountForFactory(factoryName) >= 2,
            enabled: true,
            onAssignEmployee: () =>
            {
                this.onAssignMineEmployee.call(this);
            },
            namedGroup: "mine-2"
        });

        let groupArray = [
            mine
        ];

        let result = new fabric.Group(groupArray, {
            objectCaching: false
        });

        // Set up production info
        {
            let groupArr = [];
            let groupText = new fabric.Text(this.team.currentRound < 8 ? "+2" : "+1", {
                originX: "center",
                originY: "center",
                left: 309,
                top: 510,
                fontFamily: "SpaceGrotesk-Bold",
                fontSize: 20,
                fill: "#ffffff"
            });
            groupArr.push(groupText);

            let groupIcon = await this.loadSvg(resourceIcons("./resource_white.svg"), {
                left: 264,
                top: 510,
                originY: "center"
            });
            groupIcon.scaleToWidth(28);
            groupIcon.scaleToHeight(27);
            groupArr.push(groupIcon);

            if (this.team.currentRound < 8)
            {
                let groupIcon2 = await this.loadSvg(resourceIcons("./resource_white.svg"), {
                    left: 234,
                    top: 510,
                    originY: "center"
                });
                groupIcon2.scaleToWidth(28);
                groupIcon2.scaleToHeight(27);
                groupArr.push(groupIcon2);
            }

            let group = new fabric.Group(groupArr, {});
            let groupBounds = group.getBoundingRect();
            let background = new fabric.Rect({
                top: groupBounds.top - 7,
                left: groupBounds.left - 14,
                width: groupBounds.width + 28,
                height: groupBounds.height + 14,
                rx: 4,
                ry: 4,
                fill: "#f47a1b"
            });

            let totalGroup = new fabric.Group([ background, group ], {});

            this.sectorsGroup.addWithUpdate(totalGroup);
        }

        this.sectorsGroup.addWithUpdate(result);

        this.createInfoIcon({
            left: 27,
            top: 489,
            backgroundColor: "#ffbe91",
            onClick: () =>
            {
                let container = $("<div>");

                new InfoMiningView({ container: container, mode: "info" }).render();

                Shared.Dialog.showMessageDialog({
                    title: "info.title",
                    message: container,
                    disposable: false,
                    extraModalContentClass: "info-mining-background-container"
                });
            }
        });
    }

    async setUpMicrochipFactory()
    {
        let groupArray = [];
        const factoryName = "microchip_factory";

        const factory = await this.loadSvg(dashboardIcons("./factory.svg"), {
            originX: "center",
            originY: "center",
            left: 430 + 134 / 2,
            top: 324 + 143 / 2
        });

        const icon = await this.loadSvg(resourceIcons("./microchip.svg"), {
            originX: "center",
            originY: "center",
            left: 430 + 24 + 22,
            top: 324 + 81 + 22
        });

        const upgrade = await this.loadSvg(dashboardIcons("./factory_upgrade.svg"), {
            originX: "left",
            originY: "center",
            left: 430 + 131,
            top: 324 + 82
        });

        let hasEfficiencyInvestment = InvestmentsView.hasPurchasedInvestment(this.team, { title: "microchip_efficiency" });
        let hasSubstitutionInvestment = InvestmentsView.hasPurchasedInvestment(this.team, { title: "microchip_substitution" });
        upgrade.set({ opacity: hasEfficiencyInvestment || hasSubstitutionInvestment ? 1 : INACTIVE_OPACITY });

        groupArray.push(factory);
        groupArray.push(icon);
        groupArray.push(upgrade);

        if (hasEfficiencyInvestment)
        {
            let upgradeIcon = await this.loadSvg(dashboardIcons("./resource_white with minus sign.svg"), {
                originX: "center",
                originY: "center",
                left: 612,
                top: 412
            });
            groupArray.push(upgradeIcon);
        }
        else if (hasSubstitutionInvestment)
        {
            let upgradeIcon = await this.loadSvg(dashboardIcons("./resource_substitution.svg"), {
                originX: "center",
                originY: "center",
                left: 612,
                top: 412
            });
            groupArray.push(upgradeIcon);
        }

        let redResource = await this.loadSvg(resourceIcons("./resource_red.svg"), {
            left: 574,
            top: 435
        });
        redResource.scaleToWidth(35);
        redResource.scaleToHeight(33);

        let yellowResource = await this.loadSvg(resourceIcons("./resource_yellow.svg"), {
            left: 612,
            top: 435
        });
        yellowResource.scaleToWidth(35);
        yellowResource.scaleToHeight(33);

        let purpleResource = await this.loadSvg(resourceIcons("./resource_purple.svg"), {
            left: 649,
            top: 435
        });
        purpleResource.scaleToWidth(35);
        purpleResource.scaleToHeight(33);

        await this.createEmployeeSpot({
            left: 472,
            top: 484,
            taken: this.getEmployeeCountForFactory(factoryName) >= 1,
            enabled: this.canEmployMicrochipFactory(),
            onAssignEmployee: () =>
            {
                this.onAssignMicrochipEmployee.call(this);
            },
            namedGroup: "microchip-1"
        });

        await this.createEmployeeSpot({
            left: 439,
            top: 499,
            taken: this.getEmployeeCountForFactory(factoryName) >= 2,
            enabled: this.canEmployMicrochipFactory(),
            onAssignEmployee: () =>
            {
                this.onAssignMicrochipEmployee.call(this);
            }
        });
        groupArray.push(yellowResource);
        groupArray.push(redResource);
        groupArray.push(purpleResource);

        let result = new fabric.Group(groupArray, {
            objectCaching: false
        });

        // Set up production info
        {
            let groupText = new fabric.Text("+1", {
                originX: "center",
                originY: "center",
                left: 718 + 37 * 0.5,
                top: 311 + 44 * 0.5,
                fontFamily: "SpaceGrotesk-Bold",
                fontSize: 20,
                fill: "#ffffff"
            });

            let groupIcon = await this.loadSvg(resourceIcons("./microchip.svg"), {
                left: 688,
                top: 317
            });
            groupIcon.scaleToWidth(30);
            groupIcon.scaleToHeight(30);

            let group = new fabric.Group([ groupText, groupIcon ], {});
            let groupBounds = group.getBoundingRect();
            let background = new fabric.Rect({
                top: groupBounds.top - 7,
                left: groupBounds.left - 14,
                width: groupBounds.width + 28,
                height: groupBounds.height + 14,
                rx: 4,
                ry: 4,
                fill: "#5FB6CD"
            });

            let totalGroup = new fabric.Group([ background, group ], {});

            this.sectorsGroup.addWithUpdate(totalGroup);
        }

        this.sectorsGroup.addWithUpdate(result);

        this.createInfoIcon({
            left: 376,
            top: 308,
            backgroundColor: "#C8E3EF",
            onClick: () =>
            {
                let investment = InvestmentsView.getPurchasedInvestment(this.team, { title: "microchip_factory" })

                this.view.showInvestmentDialog(investment, true);
            }
        });
    }

    async setUpSecondaryFactory()
    {
        const factoryName = "secondary_factory";
        let hasPhoneInvestment = InvestmentsView.hasPurchasedInvestment(this.team, { title: "phone_factory" });
        let hasBikeInvestment = InvestmentsView.hasPurchasedInvestment(this.team, { title: "bike_factory" });

        if (hasPhoneInvestment || hasBikeInvestment)
        {
            const factory = await this.loadSvg(dashboardIcons("./factory.svg"), {
                left: 430,
                top: 524
            });

            let iconName = hasPhoneInvestment ? "./mobile phone.svg" : "./bike.svg";
            const icon = await this.loadSvg(resourceIcons(iconName), {
                originX: "center",
                originY: "center",
                left: 472,
                top: 626
            });

            const upgrade = await this.loadSvg(dashboardIcons("./factory_upgrade.svg"), {
                left: 561,
                top: 559
            });

            await this.createEmployeeSpot({
                left: 472,
                top: 682,
                taken: this.getEmployeeCountForFactory(factoryName) >= 1,
                enabled: this.canEmploySecondaryFactory(),
                onAssignEmployee: () =>
                {
                    this.onAssignSecondaryEmployee.call(this);
                }
            });

            await this.createEmployeeSpot({
                left: 439,
                top: 698,
                taken: this.getEmployeeCountForFactory(factoryName) >= 2,
                enabled: this.canEmploySecondaryFactory(),
                onAssignEmployee: () =>
                {
                    this.onAssignSecondaryEmployee.call(this);
                }
            });

            let groupArray = [
                factory,
                icon,
                upgrade
            ];

            if (hasPhoneInvestment)
            {
                let hasEfficiencyInvestment = InvestmentsView.hasPurchasedInvestment(this.team, { title: "phone_efficiency" });
                let hasSubstitutionInvestment = InvestmentsView.hasPurchasedInvestment(this.team, { title: "phone_substitution" });

                upgrade.set({ opacity: hasEfficiencyInvestment || hasSubstitutionInvestment ? 1 : INACTIVE_OPACITY });

                if (hasEfficiencyInvestment)
                {
                    let upgradeIcon = await this.loadSvg(dashboardIcons("./resource_white with minus sign.svg"), {
                        originX: "center",
                        originY: "center",
                        left: 612,
                        top: 611
                    });
                    groupArray.push(upgradeIcon);
                }
                else if (hasSubstitutionInvestment)
                {
                    let upgradeIcon = await this.loadSvg(dashboardIcons("./resource_substitution.svg"), {
                        originX: "center",
                        originY: "center",
                        left: 612,
                        top: 611
                    });
                    groupArray.push(upgradeIcon);
                }

                let microchipResource = await this.loadSvg(resourceIcons("./microchip.svg"), {
                    left: 578,
                    top: 637
                });
                microchipResource.scaleToWidth(31);
                microchipResource.scaleToHeight(32);

                let plusText = new fabric.Textbox("+", {
                    width: 13,
                    height: 13,
                    originX: "center",
                    originY: "center",
                    left: 624,
                    top: 655,

                    fontFamily: "SpaceGrotesk-Bold",
                    fontSize: 26,
                    textAlign: "center",
                    stroke: "#162957"
                });

                let yellowResource = await this.loadSvg(resourceIcons("./resource_yellow.svg"), {
                    left: 634,
                    top: 636
                });
                yellowResource.scaleToWidth(35);
                yellowResource.scaleToHeight(33);

                groupArray.push(microchipResource);
                groupArray.push(plusText);
                groupArray.push(yellowResource);

                this.createInfoIcon({
                    left: 381,
                    top: 522,
                    backgroundColor: "#C8E3EF",
                    onClick: () =>
                    {
                        let investment = InvestmentsView.getPurchasedInvestment(this.team, { title: "phone_factory" })

                        this.view.showInvestmentDialog(investment, true);
                    }
                });
            }
            else if (hasBikeInvestment)
            {
                let hasEfficiencyInvestment = InvestmentsView.hasPurchasedInvestment(this.team, { title: "bike_efficiency" });
                let hasSubstitutionInvestment = InvestmentsView.hasPurchasedInvestment(this.team, { title: "bike_substitution" });

                upgrade.set({ opacity: hasEfficiencyInvestment || hasSubstitutionInvestment ? 1 : INACTIVE_OPACITY });

                if (hasEfficiencyInvestment)
                {
                    let upgradeIcon = await this.loadSvg(dashboardIcons("./resource_white with minus sign.svg"), {
                        originX: "center",
                        originY: "center",
                        left: 612,
                        top: 611
                    });
                    groupArray.push(upgradeIcon);
                }
                else if (hasSubstitutionInvestment)
                {
                    let upgradeIcon = await this.loadSvg(dashboardIcons("./resource_substitution.svg"), {
                        originX: "center",
                        originY: "center",
                        left: 612,
                        top: 611
                    });
                    groupArray.push(upgradeIcon);
                }

                let microchipResource = await this.loadSvg(resourceIcons("./microchip.svg"), {
                    left: 578,
                    top: 637
                });
                microchipResource.scaleToWidth(31);
                microchipResource.scaleToHeight(32);

                let plusText = new fabric.Textbox("+", {
                    width: 13,
                    height: 13,
                    originX: "center",
                    originY: "center",
                    left: 624,
                    top: 655,

                    fontFamily: "SpaceGrotesk-Bold",
                    fontSize: 26,
                    textAlign: "center",
                    stroke: "#162957"
                });

                let redResource = await this.loadSvg(resourceIcons("./resource_red.svg"), {
                    left: 634,
                    top: 636
                });
                redResource.scaleToWidth(35);
                redResource.scaleToHeight(33);

                let purpleResource = await this.loadSvg(resourceIcons("./resource_purple.svg"), {
                    left: 674,
                    top: 636
                });
                purpleResource.scaleToWidth(35);
                purpleResource.scaleToHeight(33);

                groupArray.push(microchipResource);
                groupArray.push(plusText);
                groupArray.push(redResource);
                groupArray.push(purpleResource);

                this.createInfoIcon({
                    left: 381,
                    top: 522,
                    backgroundColor: "#C8E3EF",
                    onClick: () =>
                    {
                        let investment = InvestmentsView.getPurchasedInvestment(this.team, { title: "bike_factory" })

                        this.view.showInvestmentDialog(investment, true);
                    }
                });
            }

            let result = new fabric.Group(groupArray, {
                objectCaching: false
            });

            // Set up production info
            {
                let groupText = new fabric.Text("+1", {
                    originX: "center",
                    originY: "center",
                    left: 733,
                    top: 517,
                    fontFamily: "SpaceGrotesk-Bold",
                    fontSize: 20,
                    fill: "#ffffff"
                });

                let groupIconResourceIcon = hasPhoneInvestment ? "./mobile phone.svg" : "./bike.svg";
                let groupIcon = await this.loadSvg(resourceIcons(groupIconResourceIcon), {
                    originX: "right",
                    originY: "center",
                    left: 712,
                    top: 517
                });
                groupIcon.scaleToHeight(30);

                let group = new fabric.Group([ groupText, groupIcon ], {});
                let groupBounds = group.getBoundingRect();
                let background = new fabric.Rect({
                    top: groupBounds.top - 7,
                    left: groupBounds.left - 14,
                    width: groupBounds.width + 28,
                    height: groupBounds.height + 14,
                    rx: 4,
                    ry: 4,
                    fill: "#5FB6CD"
                });

                let totalGroup = new fabric.Group([ background, group ], {});

                this.sectorsGroup.addWithUpdate(totalGroup);
            }

            this.sectorsGroup.addWithUpdate(result);
        }
        else
        {
            const factory = await this.loadSvg(dashboardIcons("./under construction_blue.svg"), {
                left: 410,
                top: 518
            });
            factory.set({ opacity: UNDER_CONSTRUCTION_OPACITY });

            let groupArray = [
                factory,
            ]

            let result = new fabric.Group(groupArray, {
                objectCaching: false
            });

            this.sectorsGroup.addWithUpdate(result);
        }

        let hasUpgradeInvestment =
            InvestmentsView.hasPurchasedInvestment(this.team, { title: "phone_efficiency" }) ||
            InvestmentsView.hasPurchasedInvestment(this.team, { title: "phone_substitution" }) ||
            InvestmentsView.hasPurchasedInvestment(this.team, { title: "bike_efficiency" }) ||
            InvestmentsView.hasPurchasedInvestment(this.team, { title: "bike_substitution" });

        await this.createVictoryPoint({
            points: 3,
            left: 716,
            top: 554,
            active: hasUpgradeInvestment
        });
    }

    async setUpMarket()
    {
        let groupArray = [];

        const market = await this.loadSvg(dashboardIcons("./market.svg"), {
            left: 811,
            top: 83
        });
        groupArray.push(market);

        const microchip = await this.loadSvg(resourceIcons("./microchip.svg"), {
            originX: "center",
            originY: "center",
            left: 1043,
            top: 206
        });
        microchip.scaleToWidth(31);
        microchip.scaleToHeight(31);
        groupArray.push(microchip);

        let hasPhoneInvestment = InvestmentsView.hasPurchasedInvestment(this.team, { title: "phone_factory" });
        let hasBikeInvestment = InvestmentsView.hasPurchasedInvestment(this.team, { title: "bike_factory" });

        if (hasPhoneInvestment)
        {
            const secondaryResource = await this.loadSvg(resourceIcons("./mobile phone.svg"), {
                originX: "center",
                originY: "center",
                left: 1043,
                top: 248
            });
            secondaryResource.scaleToHeight(40);
            groupArray.push(secondaryResource);
        }
        else if (hasBikeInvestment)
        {
            const secondaryResource = await this.loadSvg(resourceIcons("./bike.svg"), {
                originX: "center",
                originY: "center",
                left: 1043,
                top: 248
            });
            secondaryResource.scaleToWidth(31);
            groupArray.push(secondaryResource);
        }

        // left: 1067
        let microchipText = new fabric.Text("" + this.getMarketProductCount("microchip"), {
            originX: "left",
            originY: "center",
            left: 1067,
            top: 206,
            fontFamily: "SpaceGrotesk-Bold",
            fontSize: 20,
            fill: "#162957"
        });
        groupArray.push(microchipText);

        if (hasPhoneInvestment || hasBikeInvestment)
        {
            let product = hasPhoneInvestment ? "phone" : "bike";
            let secondaryResourceText = new fabric.Text("" + this.getMarketProductCount(product), {
                originX: "left",
                originY: "center",
                left: 1067,
                top: 248,
                fontFamily: "SpaceGrotesk-Bold",
                fontSize: 20,
                fill: "#162957"
            });
            groupArray.push(secondaryResourceText);
        }

        // product info
        {
            let groupArr = [];
            {
                let coinIcon = await this.loadSvg(dashboardIcons("./coins_white.svg"), {
                    originX: "center",
                    originY: "center",
                    left: 1042,
                    top: 49
                });
                groupArr.push(coinIcon);

                let usePhaseGainsText = new fabric.Text("+2/", {
                    originX: "center",
                    originY: "center",
                    left: 1080,
                    top: 49,
                    fontFamily: "SpaceGrotesk-Bold",
                    fontSize: 20,
                    fill: "#ffffff"
                });
                groupArr.push(usePhaseGainsText);

                let microchipIcon = await this.loadSvg(resourceIcons("./microchip.svg"), {
                    originX: "center",
                    originY: "center",
                    left: 1116,
                    top: 49
                });
                microchipIcon.scaleToWidth(30);
                groupArr.push(microchipIcon);
            }

            if (hasPhoneInvestment || hasBikeInvestment)
            {
                let productSetting = {
                    resourceIcon: "./mobile phone.svg",
                    price: 5,
                    scaleToWidth: 22
                }

                if (hasBikeInvestment)
                {
                    productSetting.resourceIcon = "./bike.svg";
                    productSetting.price = 7;
                    productSetting.scaleToWidth = 30;
                }

                let coinIcon = await this.loadSvg(dashboardIcons("./coins_white.svg"), {
                    originX: "center",
                    originY: "center",
                    left: 1042,
                    top: 88
                });
                groupArr.push(coinIcon);

                let usePhaseGainsText = new fabric.Text(`+${productSetting.price}/`, {
                    originX: "center",
                    originY: "center",
                    left: 1080,
                    top: 88,
                    fontFamily: "SpaceGrotesk-Bold",
                    fontSize: 20,
                    fill: "#ffffff"
                });
                groupArr.push(usePhaseGainsText);

                let microchipIcon = await this.loadSvg(resourceIcons(productSetting.resourceIcon), {
                    originX: "center",
                    originY: "center",
                    left: 1116,
                    top: 88
                });
                microchipIcon.scaleToWidth(productSetting.scaleToWidth);
                groupArr.push(microchipIcon);
            }

            let group = new fabric.Group(groupArr, {});
            let groupBounds = group.getBoundingRect();
            let background = new fabric.Rect({
                top: groupBounds.top - 11,
                left: groupBounds.left - 13,
                width: groupBounds.width + 26,
                height: groupBounds.height + 22,
                rx: 4,
                ry: 4,
                fill: "#dc4a67"
            });
            let totalGroup = new fabric.Group([ background, group ], {});
            groupArray.push(totalGroup);
        }


        let result = new fabric.Group(groupArray, {
            objectCaching: false
        });

        this.sectorsGroup.addWithUpdate(result);

        this.createInfoIcon({
            left: 813,
            top: 22,
            backgroundColor: "#f1afb4",
            onClick: () =>
            {
                let container = $("<div>");

                let type = undefined;
                let hasPhoneInvestment = InvestmentsView.hasPurchasedInvestment(this.team, { title: "phone_factory" });
                let hasBikeInvestment = InvestmentsView.hasPurchasedInvestment(this.team, { title: "bike_factory" });

                if (hasPhoneInvestment)
                {
                    type = "phone";
                }
                else if (hasBikeInvestment)
                {
                    type = "bike";
                }

                new InfoMarketView({
                    container: container,
                    secondaryType: type,
                    market: JSON.parse(this.roundOverview.currentRoundData.market)
                }).render();

                Shared.Dialog.showMessageDialog({
                    title: "info.title",
                    message: container,
                    disposable: false,
                    extraModalContentClass: "info-market-background-container"
                });
            }
        });
    }

    async setUpWasteDepot()
    {
        const wasteDepot = await this.loadSvg(dashboardIcons("./waste depot.svg"), {
            left: 27,
            top: 272
        });

        let groupArray = [
            wasteDepot
        ]

        let result = new fabric.Group(groupArray, {
            objectCaching: false
        });

        this.sectorsGroup.addWithUpdate(result);

        this.createInfoIcon({
            left: 26,
            top: 292,
            backgroundColor: "#ffe2a1",
            onClick: () =>
            {
                let container = $("<div>");

                new InfoWasteDepotView({ container: container }).render();

                Shared.Dialog.showMessageDialog({
                    title: "info.title",
                    message: container,
                    disposable: false,
                    extraModalContentClass: "info-waste-depot-background-container"
                });
            }
        });
    }

    async setUpWasteRecycling()
    {
        const factoryName = "waste_recycling";
        let hasInvestment = InvestmentsView.hasPurchasedInvestment(this.team, { title: "waste_recycling" });

        if (hasInvestment)
        {
            const wasteRecycling = await this.loadSvg(dashboardIcons("./waste recycling.svg"), {
                left: 28,
                top: 30
            });

            const production = await this.loadSvg(dashboardIcons("./waste_recycling_resources.svg"), {
                left: 223,
                top: 22
            });

            await this.createEmployeeSpot({
                left: 121,
                top: 254,
                taken: this.getEmployeeCountForFactory(factoryName) >= 1,
                enabled: true,
                onAssignEmployee: () =>
                {
                    this.onAssignWasteRecycleEmployee.call(this);
                }
            });

            await this.createEmployeeSpot({
                left: 88,
                top: 272,
                taken: this.getEmployeeCountForFactory(factoryName) >= 2,
                enabled: true,
                onAssignEmployee: () =>
                {
                    this.onAssignWasteRecycleEmployee.call(this);
                }
            });

            let groupArray = [
                wasteRecycling,
                production
            ]

            let result = new fabric.Group(groupArray, {
                objectCaching: false
            });

            this.sectorsGroup.addWithUpdate(result);

            this.createInfoIcon({
                left: 26,
                top: 22,
                backgroundColor: "#FBE3A9",
                onClick: () =>
                {
                    let investment = InvestmentsView.getPurchasedInvestment(this.team, { title: "waste_recycling" })

                    this.view.showInvestmentDialog(investment, true);
                }
            });
        }
        else
        {
            const wasteRecycling = await this.loadSvg(dashboardIcons("./under construction_yellow.svg"), {
                left: 41,
                top: 28
            });
            wasteRecycling.set({ opacity: UNDER_CONSTRUCTION_OPACITY });

            let groupArray = [
                wasteRecycling,
            ]

            let result = new fabric.Group(groupArray, {
                objectCaching: false
            });

            this.sectorsGroup.addWithUpdate(result);
        }

    }

    async setUpReuseFacility()
    {
        const factoryName = "reuse_facility";
        let hasInvestment = InvestmentsView.hasPurchasedInvestment(this.team, { title: "microchip_reuse" });

        let groupArray = [];

        if (hasInvestment)
        {
            let hasPhoneInvestment = InvestmentsView.hasPurchasedInvestment(this.team, { title: "phone_factory" });
            let hasBikeInvestment = InvestmentsView.hasPurchasedInvestment(this.team, { title: "bike_factory" });

            const reuseFacility = await this.loadSvg(dashboardIcons("./facility with car.svg"), {
                left: 400,
                top: 87
            });
            groupArray.push(reuseFacility);

            let hasProductRefurbishingInvestment = InvestmentsView.hasPurchasedInvestment(this.team, { title: "product_refurbishing" });
            const upgrade = await this.loadSvg(dashboardIcons("./facility upgrade.svg"), {
                left: 627,
                top: 129
            });
            upgrade.set({ opacity: hasProductRefurbishingInvestment ? 1 : INACTIVE_OPACITY });
            groupArray.push(upgrade);

            if (hasProductRefurbishingInvestment)
            {
                if (hasPhoneInvestment)
                {
                    let upgradeIcon = await this.loadSvg(resourceIcons("./mobile-phone-green.svg"), {
                        originX: "center",
                        originY: "center",
                        left: 680,
                        top: 182
                    });
                    groupArray.push(upgradeIcon);
                }
                else if (hasBikeInvestment)
                {
                    let upgradeIcon = await this.loadSvg(resourceIcons("./bike.svg"), {
                        originX: "center",
                        originY: "center",
                        left: 680,
                        top: 182
                    });
                    groupArray.push(upgradeIcon);
                }

                let purpleResource = await this.loadSvg(resourceIcons("./resource_purple.svg"), {
                    left: 639,
                    top: 203
                });
                purpleResource.scaleToWidth(35);
                purpleResource.scaleToHeight(33);
                groupArray.push(purpleResource);
            }

            await this.createEmployeeSpot({
                left: 680,
                top: 245,
                taken: this.getEmployeeCountForFactory(factoryName) >= 1,
                enabled: this.canEmployReuseFacility(),
                onAssignEmployee: () =>
                {
                    this.onAssignReuseEmployee.call(this);
                }
            });

            await this.createEmployeeSpot({
                left: 717,
                top: 260,
                taken: this.getEmployeeCountForFactory(factoryName) >= 2,
                enabled: this.canEmployReuseFacility(),
                onAssignEmployee: () =>
                {
                    this.onAssignReuseEmployee.call(this);
                }
            });

            // product info
            {
                let groupArr = [];
                {
                    let usePhaseGainsText = new fabric.Text("+1", {
                        originX: "center",
                        originY: "center",
                        left: 682,
                        top: 49,
                        fontFamily: "SpaceGrotesk-Bold",
                        fontSize: 20,
                        fill: "#ffffff"
                    });
                    groupArr.push(usePhaseGainsText);

                    let microchipIcon = await this.loadSvg(resourceIcons("./microchip-green.svg"), {
                        originX: "center",
                        originY: "center",
                        left: 646,
                        top: 49
                    });
                    microchipIcon.scaleToWidth(30);
                    groupArr.push(microchipIcon);
                }

                if (hasPhoneInvestment || hasBikeInvestment)
                {
                    let productSetting = {
                        resourceIcon: "./mobile-phone-green.svg",
                        scaleToWidth: 22
                    }

                    if (hasBikeInvestment)
                    {
                        productSetting.resourceIcon = "./bike.svg";
                        productSetting.price = 7;
                        productSetting.scaleToWidth = 30;
                    }

                    let usePhaseGainsText = new fabric.Text("+1", {
                        originX: "center",
                        originY: "center",
                        left: 682,
                        top: 88,
                        fontFamily: "SpaceGrotesk-Bold",
                        fontSize: 20,
                        fill: "#ffffff"
                    });
                    groupArr.push(usePhaseGainsText);

                    let microchipIcon = await this.loadSvg(resourceIcons(productSetting.resourceIcon), {
                        originX: "center",
                        originY: "center",
                        left: 646,
                        top: 88
                    });
                    microchipIcon.scaleToWidth(productSetting.scaleToWidth);
                    groupArr.push(microchipIcon);
                }

                let group = new fabric.Group(groupArr, {});
                let groupBounds = group.getBoundingRect();
                let background = new fabric.Rect({
                    top: groupBounds.top - 11,
                    left: groupBounds.left - 13,
                    width: groupBounds.width + 26,
                    height: groupBounds.height + 22,
                    rx: 4,
                    ry: 4,
                    fill: "#aec902"
                });
                let totalGroup = new fabric.Group([ background, group ], {});
                groupArray.push(totalGroup);
            }

            let result = new fabric.Group(groupArray, {
                objectCaching: false
            });

            this.sectorsGroup.addWithUpdate(result);

            this.createInfoIcon({
                left: 383,
                top: 22,
                backgroundColor: "#e7efa7",
                onClick: () =>
                {
                    let investment = InvestmentsView.getPurchasedInvestment(this.team, { title: "microchip_reuse" })

                    this.view.showInvestmentDialog(investment, true);
                }
            });
        }
        else
        {
            const reuseFacility = await this.loadSvg(dashboardIcons("./under construction_lightgreen.svg"), {
                left: 436,
                top: 30
            });
            reuseFacility.set({ opacity: UNDER_CONSTRUCTION_OPACITY });

            let groupArray = [
                reuseFacility
            ]

            let result = new fabric.Group(groupArray, {
                objectCaching: false
            });

            this.sectorsGroup.addWithUpdate(result);
        }

        await this.createVictoryPoint({
            points: 3,
            left: 721,
            top: 20,
            active: InvestmentsView.hasPurchasedInvestment(this.team, { title: "product_refurbishing" })
        });
    }

    async setUpRental()
    {
        const factoryName = "rental";
        let hasInvestment = InvestmentsView.hasPurchasedInvestment(this.team, { title: "rental_maintenance" });
        let groupArray = [];

        if (hasInvestment)
        {
            const rentalFacility = await this.loadSvg(dashboardIcons("./rental service.svg"), {
                left: 816,
                top: 392
            });
            groupArray.push(rentalFacility);

            let hasRepairWorkshop = InvestmentsView.hasPurchasedInvestment(this.team, { title: "repair_workshop" });
            const upgrade = await this.loadSvg(dashboardIcons("./rental service upgrade.svg"), {
                left: 1013,
                top: 443
            });
            groupArray.push(upgrade);
            upgrade.set({ opacity: hasRepairWorkshop ? 1 : INACTIVE_OPACITY });

            if (hasRepairWorkshop)
            {
                const upgradeIcon = await this.loadSvg(dashboardIcons("./rental service upgrade icon.svg"), {
                    originX: "center",
                    originY: "center",
                    left: 1080,
                    top: 483
                });
                groupArray.push(upgradeIcon);
            }

            await this.createEmployeeSpot({
                left: 851,
                top: 539,
                taken: this.getEmployeeCountForFactory(factoryName) >= 1,
                enabled: this.canRentProducts(),
                onAssignEmployee: () =>
                {
                    this.onAssignRentalEmployee.call(this);
                }
            });

            await this.createEmployeeSpot({
                left: 818,
                top: 554,
                taken: this.getEmployeeCountForFactory(factoryName) >= 2,
                enabled: this.canRentProducts(),
                onAssignEmployee: () =>
                {
                    this.onAssignRentalEmployee.call(this);
                }
            });

            let rentalPhaseText = new fabric.Text("USE PHASE", {
                originX: "center",
                originY: "center",
                left: 923,
                top: 613,
                fontFamily: "SpaceGrotesk-Bold",
                fontSize: 14,
                fill: "#162957"
            });
            groupArray.push(rentalPhaseText);

            for (let [ index, phase ] of [ 1, 2, 3 ].entries())
            {
                // 97 including ">"
                let xPos = 794 + index * 97;
                let yPosCenter = 664;

                let phaseIndexText = new fabric.Text(`${phase}`, {
                    originX: "center",
                    originY: "center",
                    left: xPos + 27,
                    top: yPosCenter - 33,
                    fontFamily: "SpaceGrotesk-Bold",
                    fontSize: 14,
                    fill: "#162957"
                });
                groupArray.push(phaseIndexText);

                let productIconSetting = {
                    resourceIcon: "./mobile phone.svg",
                    scaleToWidth: 24,
                };
                if (InvestmentsView.hasPurchasedInvestment(this.team, { title: "bike_factory" }))
                {
                    productIconSetting.resourceIcon = "./bike.svg";
                    productIconSetting.scaleToWidth = 34;
                }

                let productIcon = await this.loadSvg(resourceIcons(productIconSetting.resourceIcon), {
                    originX: "center",
                    originY: "center",
                    left: xPos + 27,
                    top: yPosCenter
                });
                productIcon.scaleToWidth(productIconSetting.scaleToWidth);
                groupArray.push(productIcon);

                let phaseCountText = new fabric.Text(`${this.getRentalProductCountForPhase(phase)}`, {
                    originX: "center",
                    originY: "center",
                    left: xPos + 66,
                    top: yPosCenter,
                    fontFamily: "SpaceGrotesk-Bold",
                    fontSize: 18,
                    fill: "#162957"
                });
                groupArray.push(phaseCountText);

                let nextIcon = await this.loadSvg(dashboardIcons("./arrow_green.svg"), {
                    originX: "right",
                    originY: "center",
                    left: xPos + 97,
                    top: yPosCenter
                });
                nextIcon.scaleToHeight(15);
                groupArray.push(nextIcon);
            }

            let wasteIcon = await this.loadSvg(dashboardIcons("./garbage container_white.svg"), {
                originX: "center",
                originY: "center",
                left: 1112,
                top: 663
            });
            groupArray.push(wasteIcon);

            // Set up rental info
            {
                let groupArr = [];
                let usePhaseGainsText = new fabric.Text(`+${InvestmentsView.hasPurchasedInvestment(this.team, { title: "phone_factory" }) ? 3 : 4}/ USE PHASE`, {
                    originX: "right",
                    originY: "center",
                    left: 1132,
                    top: 556,
                    fontFamily: "SpaceGrotesk-Bold",
                    fontSize: 20,
                    fill: "#ffffff"
                });
                groupArr.push(usePhaseGainsText);

                let group = new fabric.Group(groupArr, {});
                let groupBounds = group.getBoundingRect();
                let coinsIcon = await this.loadSvg(dashboardIcons("./coins_white.svg"), {
                    originX: "right",
                    originY: "center",
                    top: 556,
                    left: groupBounds.left - 5
                });
                group.addWithUpdate(coinsIcon);

                groupBounds = group.getBoundingRect();
                let background = new fabric.Rect({
                    top: groupBounds.top - 7,
                    left: groupBounds.left - 13,
                    width: groupBounds.width + 26,
                    height: groupBounds.height + 14,
                    rx: 4,
                    ry: 4,
                    fill: "#73b858"
                });
                let totalGroup = new fabric.Group([ background, group ], {});
                groupArray.push(totalGroup);
            }

            if (hasRepairWorkshop)
            {
                let previousIcon = await this.loadSvg(dashboardIcons("./arrow_green.svg"), {
                    originX: "center",
                    originY: "center",
                    left: 923,
                    top: 705
                });
                previousIcon.scaleToHeight(15);
                previousIcon.set("flipX", true);
                groupArray.push(previousIcon);

                let linePoints = [
                    { x: 827, y: 686 },
                    { x: 827, y: 705 },
                    { x: 1017, y: 705 },
                    { x: 1017, y: 686 }
                ];
                let lines = this.drawLine(linePoints, {
                    stroke: "#589045"
                });
                groupArray = groupArray.concat(lines);

                let minusSign = new fabric.Text("-", {
                    originX: "center",
                    originY: "center",
                    left: 1029,
                    top: 696,
                    fontFamily: "SpaceGrotesk-Bold",
                    fontSize: 20,
                    fill: "#162957"
                });
                groupArray.push(minusSign);

                let resourceCostIcon = await this.loadSvg(resourceIcons("./resource_red.svg"), {
                    originX: "center",
                    originY: "center",
                    left: 1049,
                    top: 694
                });
                resourceCostIcon.scaleToHeight(22);
                groupArray.push(resourceCostIcon);
            }

            let result = new fabric.Group(groupArray, {
                objectCaching: false
            });

            this.sectorsGroup.addWithUpdate(result);

            this.createInfoIcon({
                left: 814,
                top: 339,
                backgroundColor: "#d1edb7",
                onClick: () =>
                {
                    let investment = InvestmentsView.getPurchasedInvestment(this.team, { title: "rental_maintenance" })

                    this.view.showInvestmentDialog(investment, true);
                }
            });
        }
        else
        {
            const rentalFacility = await this.loadSvg(dashboardIcons("./under construction_green.svg"), {
                left: 808,
                top: 369
            });
            rentalFacility.set({ opacity: UNDER_CONSTRUCTION_OPACITY });

            let groupArray = [
                rentalFacility
            ]

            let result = new fabric.Group(groupArray, {
                objectCaching: false
            });

            this.sectorsGroup.addWithUpdate(result);
        }

        await this.createVictoryPoint({
            points: 4,
            left: 1102,
            top: 343,
            active: InvestmentsView.hasPurchasedInvestment(this.team, { title: "repair_workshop" })
        });
    }

    createInfoIcon(infoIcon)
    {
        infoIcon = { ...infoIcon, ...this.layoutData.infoSettings }; // merging two objects

        let infoIconBackground = new fabric.Circle({
            radius: infoIcon.radius,
            fill: infoIcon.backgroundColor,
            left: infoIcon.left,
            top: infoIcon.top,
        });

        let infoIconText = new fabric.Textbox("i", {
            width: infoIconBackground.radius * 2,
            height: infoIconBackground.radius * 2,
            originX: "center",
            originY: "center",
            left: infoIcon.left + infoIconBackground.radius,
            top: infoIcon.top + infoIconBackground.radius,

            fontFamily: infoIcon.text.fontFamily,
            fontSize: infoIcon.text.fontSize,
            textAlign: infoIcon.text.alignment,
            stroke: infoIcon.text.color
        });

        let infoIconGroup = new fabric.Group([ infoIconBackground, infoIconText ], {
            hasBorders: false,
            hasControls: false,
            selectable: false,
            reference: false,
            moveable: false,
            visible: true,
            hoverCursor: "pointer"
        });

        this.infoIcons.push(infoIconGroup);
        this.canvas.add(infoIconGroup);

        infoIconGroup.on("mousedown", (options) =>
        {
            if (infoIcon.onClick)
            {
                infoIcon.onClick.call(this);
            }
        });
    }

    drawLine(linePoints, options)
    {
        let lines = [];

        for (let i = 0; i < linePoints.length - 1; ++i)
        {
            let pointA = linePoints[i];
            let pointB = linePoints[i + 1];

            let line = new fabric.Line([ pointA.x, pointA.y, pointB.x, pointB.y ], {
                originX: "center",
                originY: "center",
                stroke: options.stroke || "#589045",
                strokeWidth: options.strokeWidth || 2,
                hasControls: false,
                hasBorders: false,
                lockMovementX: true,
                lockMovementY: true,
                hoverCursor: "default"
            });
            lines.push(line);
        }
        return lines;
    }

    async createVictoryPoint(vp)
    {
        let image = await this.loadSvg(resourceIcons("./trophy.svg"), {
            left: vp.left,
            top: vp.top
        });

        let pointsText = new fabric.Textbox(`${vp.points}`, {
            originX: "center",
            originY: "center",
            left: vp.left + image.width / 2,
            top: vp.top + image.width / 2 - 8,
            width: image.width,
            height: image.width,
            fontFamily: "SpaceGrotesk-Bold",
            fontSize: 20,
            textAlign: "center"
        });

        let group = new fabric.Group([ image, pointsText ], {
            hasBorders: false,
            hasControls: false,
            selectable: false,
            reference: false,
            moveable: false,
            visible: true,
            hoverCursor: "default"
        });
        group.set({ opacity: vp.active ? 1 : INACTIVE_OPACITY });

        this.canvas.add(group);
    }

    async createEmployeeSpot(spot)
    {
        let group = new fabric.Group([], {
            hasBorders: false,
            hasControls: false,
            selectable: false,
            reference: false,
            moveable: false,
            visible: true,
            hoverCursor: spot.taken ? "default" : "pointer"
        });

        const employeeSpot = await this.loadSvg(dashboardIcons("./employee_spot.svg"), {
            left: spot.left,
            top: spot.top
        });
        group.addWithUpdate(employeeSpot);

        if (spot.taken)
        {
            const employee = await this.loadSvg(resourceIcons("./employee.svg"), {
                originX: "center",
                originY: "bottom",
                left: spot.left + employeeSpot.width / 2,
                top: spot.top + employeeSpot.height / 2
            });

            group.addWithUpdate(employee);
        }
        else
        {
            let freeEmployees = this.getFreeEmployees();
            if (freeEmployees > 0 && spot.enabled)
            {
                group.on("mousedown", (options) =>
                {
                    log.info("On mouse down on employee spot");
                    spot.onAssignEmployee.call(this);
                });
            }
            else
            {
                group.set({ opacity: INACTIVE_OPACITY });
                group.hoverCursor = "default";

                group.on("mousedown", (options) =>
                {
                    this.onInactiveSpotClick.call(this);
                });
            }
        }

        group.namedGroup = spot.namedGroup;
        group.spotCanBeUsed = !spot.taken && spot.enabled && this.getFreeEmployees() > 0;

        this.employeeSpots.push(group);
        this.canvas.add(group);

        // this.employeeSpotGroup.addWithUpdate(group);
    }

    enableTutorialNamedEmployeeSpot(namedGroup)
    {
        for (let group of this.employeeSpots)
        {
            if (group.namedGroup !== namedGroup)
            {
                group.set({ opacity: INACTIVE_OPACITY });
                group.hoverCursor = "default";
                group.off("mousedown");
            }
            else
            {
                let delay = 10000;
                let duration = 500;
                let scale = 1.5;
                let pulseInterval = -1;

                let pulse = (up) =>
                {
                    try
                    {
                        let position = group.translateToGivenOrigin({
                            x: group.left,
                            y: group.top
                        }, group.originX, group.originY, "center", "bottom");
                        group.originX = "center";
                        group.originY = "bottom";
                        group.left = position.x;
                        group.top = position.y;

                        if (up)
                        {
                            group.animate({
                                scaleX: scale,
                                scaleY: scale
                            }, {
                                duration: duration,
                                onChange: this.canvas.renderAll.bind(this.canvas),
                            });
                        }
                        else
                        {
                            group.animate({
                                scaleX: 1,
                                scaleY: 1
                            }, {
                                duration: duration,
                                onChange: this.canvas.renderAll.bind(this.canvas),
                            });
                        }
                    }
                    catch (error)
                    {
                        clearInterval(pulseInterval);
                    }
                }

                setTimeout(() =>
                {
                    pulseInterval = setInterval(() =>
                    {
                        pulse(Math.floor(Date.now() / duration) % 2 === 0);
                    }, duration)
                    pulse(Math.floor(Date.now() / duration) % 2 === 0);


                    let textInfo = this.layoutData.infoSettings.text;
                    let position = group.translateToGivenOrigin({
                        x: group.left,
                        y: group.top
                    }, group.originX, group.originY, "center", "bottom");

                    let clickText = new fabric.Textbox("Click!", {
                        originX: "center",
                        originY: "center",
                        left: position.x,
                        top: position.y - 17,
                        fontFamily: textInfo.fontFamily,
                        fontSize: 12,
                        textAlign: textInfo.alignment,
                        stroke: "#162957"
                    });

                    group.addWithUpdate(clickText);

                }, Math.max(delay - duration, 0));
            }
        }

        this.canvas.requestRenderAll();
    }

    drawDebugInfo(text, position, color, offset = 0)
    {
        if (__DEVELOPMENT__ && false)
        {
            let debugText = new fabric.Textbox(text, {
                originX: "center",
                originY: "center",
                left: position.x + offset,
                top: position.y + offset,

                fontFamily: "Rubik",
                fontSize: 24,
                textAlign: "center",
                stroke: color,
                fill: color
            });

            this.debugGroup.addWithUpdate(debugText);
        }
    }

    getSectorForKey(key)
    {
        for (let i = 0; i < this.sectors.length; i++)
        {
            let sector = this.sectors[i];

            if (sector.key === key)
            {
                return sector;
            }
        }

        log.error("Failed to find sector for key [%s]", key);

        return null;
    }

    loadSvg(source, svgOptions, fromString)
    {
        return new Promise((resolve, reject) =>
        {
            function onComplete(objects, options)
            {
                if (utils.isNotNull(svgOptions))
                {
                    options = { ...options, ...svgOptions };
                }

                let obj = fabric.util.groupSVGElements(objects, options);

                obj.originX = options.originX || "left";
                obj.originY = options.originY || "top";
                obj.top = options.top || 0;
                obj.left = options.left || 0;

                obj.width = options.width || obj.width;
                obj.height = options.height || obj.height;

                // obj.evented = that.evented;
                // obj.setCoords();

                resolve(obj);
            }

            function addSvgProperties(el, obj)
            {
                obj.id = el.getAttribute("id");
            }

            if (fromString)
            {
                fabric.loadSVGFromString(source, onComplete, addSvgProperties);
            }
            else
            {
                fabric.loadSVGFromURL(source, onComplete, addSvgProperties);
            }
        });
    }

    loadImage(source, options)
    {
        return new Promise((resolve, reject) =>
        {
            let img = new Image();
            img.src = "";
            img.onload = function()
            {
                let fabricImage = new fabric.Image(this, options);
                resolve(fabricImage);
            };
            img.onerror = reject;
            img.src = source;
        });
    }

    set income(value)
    {
        if (!utils.isNumber(value))
        {
            value = parseInt(value);
            value = utils.isNumber(value) ? value : 0;
        }

        this._income = value;
    }

    get income()
    {
        return this._income;
    }

    reset()
    {
        this.abortAnimations();
    }

    abortAnimations()
    {
        this.shouldAbortAnimations = true;

        for (let i = 0; i < this.sectors.length; i++)
        {
            this.sectors[i].abortAnimations();
        }
    }

    addGridToCanvas(gridSize)
    {
        let width = this.canvas.width,
            height = this.canvas.height;

        let gridGroup = [];

        for (let i = 0; i < (width / gridSize); i++)
        {
            let x = i * gridSize;

            gridGroup.push(new fabric.Line([ x, 0, x, height ], { stroke: "#ddd" }));

            gridGroup.push(new fabric.Text(x + "", {
                left: x,
                top: 0,
                textAlign: "center",
                fontSize: 10,
                fontFamily: "Helvetica Nue, Helvetica, Sans-Serif, Arial, Trebuchet MS"
            }));
        }

        for (let i = 0; i < (height / gridSize); i++)
        {
            let y = i * gridSize;

            gridGroup.push(new fabric.Line([ 0, y, width, y ], { stroke: "#ddd" }));

            gridGroup.push(new fabric.Text(y + "", {
                left: 0,
                top: y,
                textAlign: "center",
                fontSize: 10,
                fontFamily: "Helvetica Nue, Helvetica, Sans-Serif, Arial, Trebuchet MS"
            }));
        }

        this.canvas.add(new fabric.Group(gridGroup, {
            hasBorders: false,
            hasControls: false,
            selectable: false,
            evented: false,
            reference: false,
            movable: false
        }));
    }

    destroy()
    {
        this.canvas.clear();
        this.canvas.dispose();
        this.canvas = null;
    }

    setUpAutoResize()
    {
        this.fitToContainer();

        let resizeFunction = utils.debounce(this.fitToContainer, 250, false, this);
        window.addEventListener("resize", resizeFunction, false);
    }

    fitToContainer(e)
    {
        // Note: using fabric canvas zoom causes blurriness for some reason, manually resize canvas doesn't (not sure if this breaks something else though)
        // Note²: using `imageSmoothingEnabled: false` has an even better result (event with the fabricjs zooming)
        // Best to worst quality (sharpness):
        //    1. imageSmoothingEnabled: false with fabricjs zooming
        //    2. imageSmoothingEnabled: false with manual canvas scaling
        //    3. imageSmoothingEnabled: true (default) with manual canvas scaling
        //    4. imageSmoothingEnabled: true (default) with fabricjs zooming
        let $window = $(window);
        // let containerWidth = Math.min(this.layoutData.size.width, this.containerElement.width());
        // let windowHeight = Math.min(this.layoutData.size.height, $window.height());
        let containerWidth = Math.min(this.layoutData.size.width, this.containerElement.width());
        let windowHeight = Math.min(this.layoutData.size.height, this.containerElement.height());

        if (utils.isFunction(this.availableCanvasSize))
        {
            let availableSize = this.availableCanvasSize(this);
            containerWidth = utils.isNotNull(availableSize.width) ? availableSize.width : containerWidth;
            windowHeight = utils.isNotNull(availableSize.height) ? availableSize.height : windowHeight;
        }

        let scaleRatio = Math.min(containerWidth / this.layoutData.size.width, windowHeight / this.layoutData.size.height);

        log.debug("Resizing canvas to fit container:\nWidth: %f\nHeight: %f\nScale: %f", this.containerElement.width(), this.containerElement.height(), scaleRatio);

        if (scaleRatio > 0)
        {
            let oldWidth = this.canvas.width,
                oldHeight = this.canvas.height;

            let newWidth = this.layoutData.size.width * scaleRatio,
                newHeight = this.layoutData.size.height * scaleRatio;

            this.canvas.setDimensions({ width: newWidth, height: newHeight });
            this.canvas.setZoom(scaleRatio);

            if (utils.isFunction(this.onCanvasSizeChanged))
            {
                this.onCanvasSizeChanged(oldWidth, oldHeight, newWidth, newHeight);
            }
        }

        // let initialWidth = this.layoutData.size.width;
        // let initialHeight = this.layoutData.size.height;
        //
        // let $container = this.containerElement;
        //
        // let containerElement = this.containerElement.find(".canvas-container");
        //
        // resizeCanvas($container.find("canvas").get(0));
        // resizeCanvas($container.find("canvas").get(1));
        //
        // function resizeCanvas(canvas)
        // {
        //   let newWidth = null;
        //   let newHeight = null;
        //
        //   let $window = $(window);
        //   let windowWidth = $window.width(),
        //     windowHeight = $window.height(),
        //     windowRatio = windowWidth / windowHeight;
        //
        //   let width = Math.min($container.width(), initialWidth);
        //   let height = initialHeight / initialWidth * width;
        //
        //   let wratio = width / height;
        //   let ratio = initialWidth / initialHeight;
        //
        //   if(windowRatio < wratio)
        //   {
        //     newWidth = width;
        //     newHeight = width / ratio;
        //   }
        //   else
        //   {
        //     height = Math.max(windowHeight, initialHeight);
        //     width = initialWidth / initialHeight * height;
        //
        //     newWidth = width;
        //     newHeight = height;
        //   }
        //
        //   if(newWidth !== null && newHeight !== null)
        //   {
        //     canvas.style.width = newWidth + "px";
        //     canvas.style.height = newHeight + "px";
        //
        //     containerElement.css("width", newWidth + "px");
        //     containerElement.css("height", newHeight + "px");
        //   }
        // }
    }

    getFactoryForKey(key)
    {
        if (utils.isNull(key)) return null;

        return this.factories.find(f => f.key === key);
    }

    getFactoryForName(name)
    {
        if (utils.isNull(name)) return null;

        return this.factories.find(f => f.name === name);
    }

    getFreeEmployees()
    {
        return this.roundOverview.currentRoundData.employees - this.roundOverview.currentRoundData.usedEmployees;
    }

    getEmployeeCountForFactory(factoryName)
    {
        try
        {
            let factoryRoundData = this.team.factoryRoundData.find(e => e.roundIndex === this.team.currentRound && e.factoryKey === this.getFactoryForName(factoryName).key);

            return factoryRoundData !== undefined ? factoryRoundData.usedEmployees : 0;
        }
        catch (e)
        {
            log.error(e);
        }
        return 0;
    }

    canEmployMicrochipFactory()
    {
        let red = this.roundOverview.currentRoundData.resourceRed;
        let yellow = this.roundOverview.currentRoundData.resourceYellow;
        let purple = this.roundOverview.currentRoundData.resourcePurple;

        if (InvestmentsView.hasPurchasedInvestment(this.team, { title: "microchip_efficiency" }))
        {
            return Math.min(red, 1) + Math.min(yellow, 1) + Math.min(purple, 1) >= 2;
        }

        if (InvestmentsView.hasPurchasedInvestment(this.team, { title: "microchip_substitution" }))
        {
            return Math.min(red, 2) + Math.min(yellow, 2) + Math.min(purple, 2) >= 3;
        }

        return red > 0 && yellow > 0 && purple > 0;
    }

    canEmploySecondaryFactory()
    {
        if (this.roundOverview.currentRoundData.resourceMicrochip > 0)
        {
            if (InvestmentsView.hasPurchasedInvestment(this.team, { title: "phone_factory" }))
            {
                if (InvestmentsView.hasPurchasedInvestment(this.team, { title: "phone_efficiency" }))
                {
                    return true;
                }

                if (InvestmentsView.hasPurchasedInvestment(this.team, { title: "phone_substitution" }))
                {
                    return this.roundOverview.currentRoundData.resourceRed > 0 || this.roundOverview.currentRoundData.resourceYellow > 0 || this.roundOverview.currentRoundData.resourcePurple > 0;
                }

                return this.roundOverview.currentRoundData.resourceYellow > 0;
            }

            if (InvestmentsView.hasPurchasedInvestment(this.team, { title: "bike_factory" }))
            {
                if (InvestmentsView.hasPurchasedInvestment(this.team, { title: "bike_efficiency" }))
                {
                    return this.roundOverview.currentRoundData.resourceRed > 0 || this.roundOverview.currentRoundData.resourcePurple > 0;
                }

                if (InvestmentsView.hasPurchasedInvestment(this.team, { title: "bike_substitution" }))
                {
                    let red = Math.min(this.roundOverview.currentRoundData.resourceRed, 2);
                    let yellow = Math.min(this.roundOverview.currentRoundData.resourceYellow, 1);
                    let purple = Math.min(this.roundOverview.currentRoundData.resourcePurple, 2);

                    return red + yellow + purple >= 2;
                }

                return this.roundOverview.currentRoundData.resourceRed > 0 && this.roundOverview.currentRoundData.resourcePurple > 0;
            }

        }

        return false;
    }

    canEmployReuseFacility()
    {
        if (this.getMarketProductCount("microchip") > 0)
        {
            return true;
        }

        if (InvestmentsView.hasPurchasedInvestment(this.team, { title: "product_refurbishing" }))
        {
            if (InvestmentsView.hasPurchasedInvestment(this.team, { title: "phone_factory" }))
            {
                return this.getMarketProductCount("phone") > 0 && this.roundOverview.currentRoundData.resourcePurple > 0;
            }

            if (InvestmentsView.hasPurchasedInvestment(this.team, { title: "bike_factory" }))
            {
                return this.getMarketProductCount("bike") > 0 && this.roundOverview.currentRoundData.resourcePurple > 0;
            }
        }

        return false;
    }

    canRentProducts()
    {
        if (InvestmentsView.hasPurchasedInvestment(this.team, { title: "phone_factory" }) && this.roundOverview.currentRoundData.resourcePhone > 0)
        {
            return true;
        }

        if (InvestmentsView.hasPurchasedInvestment(this.team, { title: "bike_factory" }) && this.roundOverview.currentRoundData.resourceBike > 0)
        {
            return true;
        }

        let rental = JSON.parse(this.roundOverview.currentRoundData.rental);
        if (rental)
        {
            return rental.phase1 + rental.phase2 > 0 || (rental.phase3 > 0 && this.roundOverview.currentRoundData.resourceRed > 0);
        }
        return false;
    }

    getRentalProductCountForPhase(phase)
    {
        let rental = JSON.parse(this.roundOverview.currentRoundData.rental);

        if (!rental)
        {
            return 0;
        }

        switch (phase)
        {
            case 1:
                return rental.phase1;
            case 2:
                return rental.phase2;
            case 3:
                return rental.phase3;
        }
        return 0;
    }

    getMarketProductCount(product)
    {
        let market = JSON.parse(this.roundOverview.currentRoundData.market);

        if (!market)
        {
            return 0;
        }

        switch (product)
        {
            case "microchip":
                return market.microchip || 0;
            case "phone":
                return market.phone || 0;
            case "bike":
                return market.bike || 0;
        }

        return 0;
    }

    getFreeEmployeeSpotCount()
    {
        return this.employeeSpots.filter(group => group.spotCanBeUsed).length;
    }
}

export default BusinessModel;



// WEBPACK FOOTER //
// ./app/js/components/business-model/business-model.js